import React, {
  createContext,
  useContext,
  FC,
  useEffect,
  useCallback,
} from 'react';
import { useOrderFilterTagsActions } from 'clientState/providers/OrderedFilterTagsProvider';
import { usePerformedSearchRequest } from 'clientState/providers/SearchRequestProvider';

export type FlightDirection = 'returning' | 'going';

type PinnedSegment = {
  direction: FlightDirection;
  destinationCity: string;
  departureDate: string;
};

type PinnedSegmentState = { [segmentId: string]: PinnedSegment };

type PinSegmentProviderType = {
  pinnedSegments: PinnedSegmentState;
  pinnedSegmentIds: string[];
  isPinnedSegmentApplied: boolean;
  resetPinnedSegments: () => void;
  removePinnedSegment: (id: string) => void;
  applyPinnedSegmentsFilter: (
    pinnedSegmentId: string,
    direction: FlightDirection,
    destinationCity: string,
    destinationDate: string,
    index: number
  ) => void;
};

const ALIAS = 'pinSegment';

export const PinSegmentProviderContext = createContext<PinSegmentProviderType>(
  {} as PinSegmentProviderType
);

export const usePinSegment = (): PinSegmentProviderType =>
  useContext(PinSegmentProviderContext);

const PinSegmentProvider: FC = ({ children }) => {
  const searchRequest = usePerformedSearchRequest();
  const { tripType } = searchRequest || {};
  const { add, remove, clearAllStartsWith } = useOrderFilterTagsActions();
  const [pinnedSegments, setPinnedSegmentIds] =
    React.useState<PinnedSegmentState>({});
  const resetPinnedSegments = useCallback((): void => {
    clearAllStartsWith(ALIAS);
    setPinnedSegmentIds({});
  }, [clearAllStartsWith]);
  const removePinnedSegment = (pinnedSegmentId: string): void => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { [pinnedSegmentId]: _, ...rest } = pinnedSegments;
    setPinnedSegmentIds(rest);
  };

  const applyPinnedSegmentsFilter = (
    pinnedSegmentId: string,
    direction: FlightDirection,
    destinationCity: string,
    departureDate: string,
    index: number
  ): void => {
    const alias = [
      ALIAS,
      direction,
      tripType === 'MULTIPLE_DESTINATIONS' ? destinationCity : null,
      tripType === 'MULTIPLE_DESTINATIONS' ? departureDate : null,
      tripType === 'MULTIPLE_DESTINATIONS' ? index : null,
    ].join('_');
    const segment = { pinnedSegmentId, direction, destinationCity };

    setPinnedSegmentIds((prevSegments) => {
      // If the segment is already pinned, we remove it
      if (prevSegments[pinnedSegmentId]) {
        remove({
          alias,
        });
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [pinnedSegmentId]: _, ...rest } = pinnedSegments;
        return rest;
      }
      // Otherwise, we add it
      add({
        alias: alias,
        segment: segment,
        undo: () => {
          removePinnedSegment(pinnedSegmentId);
          remove({
            alias: alias,
            segment,
          });
        },
      });
      return {
        ...prevSegments,
        [pinnedSegmentId]: {
          direction,
          destinationCity,
          departureDate,
        },
      };
    });
  };

  useEffect(() => {
    resetPinnedSegments();
  }, [searchRequest, resetPinnedSegments]);

  const value = {
    pinnedSegments: pinnedSegments,
    pinnedSegmentIds: Object.keys(pinnedSegments),
    isPinnedSegmentApplied: Object.keys(pinnedSegments).length > 0,
    resetPinnedSegments,
    applyPinnedSegmentsFilter,
    removePinnedSegment,
  };

  return (
    <PinSegmentProviderContext.Provider value={value}>
      {children}
    </PinSegmentProviderContext.Provider>
  );
};

export const useIsPinnedSegmentApplied = (): boolean => {
  const { isPinnedSegmentApplied } = usePinSegment();

  return isPinnedSegmentApplied;
};

export default PinSegmentProvider;
